import { orderAnomalyTerm, orderStatusTerm } from "@billiard/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { OperatorIdentity } from "./api";
import {
  getOrderReceivableBusinessMetricDetail,
  orderReceivableBusinessMetricDetailQueryKey,
  type orderReceivableBusinessMetricDetailRouteFromSearch,
} from "./orderReceivableBusinessMetricDetail";

export type OrderReceivableBusinessMetricDetailRoute = ReturnType<
  typeof orderReceivableBusinessMetricDetailRouteFromSearch
>;

type OrderReceivableDetailLoader = typeof getOrderReceivableBusinessMetricDetail;

type OrderReceivableBusinessMetricDetailPanelProps = {
  operator: OperatorIdentity;
  effectiveRoles: readonly OperatorIdentity["role"][];
  route: Exclude<OrderReceivableBusinessMetricDetailRoute, { kind: "NONE" }>;
  loader?: OrderReceivableDetailLoader;
};

function formatMoney(fen: number): string {
  const amount = `¥${(Math.abs(fen) / 100).toFixed(2)}`;
  return fen < 0 ? `-${amount}` : amount;
}

function formatSignedMoney(fen: number): string {
  if (fen > 0) return `+${formatMoney(fen)}`;
  return formatMoney(fen);
}

function formatTime(timestamp: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "medium",
    hour12: false,
    timeZone: "Asia/Shanghai",
  }).format(new Date(timestamp));
}

function AuthorizedOrderReceivableBusinessMetricDetail({
  operator,
  route,
  loader,
}: {
  operator: OperatorIdentity;
  route: Extract<OrderReceivableBusinessMetricDetailRoute, { kind: "DETAIL" }>;
  loader: OrderReceivableDetailLoader;
}): React.JSX.Element {
  const context = {
    tenantId: operator.tenantId,
    storeId: operator.storeId,
    operatorId: operator.id,
  };
  const detail = useQuery({
    queryKey: orderReceivableBusinessMetricDetailQueryKey(context, route.businessDate),
    queryFn: ({ signal }) => loader(route.businessDate, context, signal),
    refetchOnMount: "always",
    retry: false,
  });
  const responseMatchesSelection =
    detail.data?.tenantId === operator.tenantId &&
    detail.data.storeId === operator.storeId &&
    detail.data.filters.dateFrom === route.businessDate &&
    detail.data.filters.dateTo === route.businessDate;
  const visibleDetail =
    !detail.isFetching && !detail.isError && responseMatchesSelection ? detail.data : undefined;
  const responseUnsafe =
    !detail.isFetching && detail.data !== undefined && !responseMatchesSelection;

  return (
    <main className="commercial-desk" aria-label="营业日订单应收完整明细">
      <header className="commerce-header">
        <div>
          <div className="button-row">
            <Link
              className="button secondary compact"
              to={`/owner?businessDate=${encodeURIComponent(route.businessDate)}`}
            >
              返回经营指标
            </Link>
            <Link className="button secondary compact" to="/orders">
              返回订单页
            </Link>
          </div>
          <small>{route.businessDate} · 全部订单权威核账结果</small>
          <h1>营业日订单应收明细</h1>
          <p>
            <strong>系统应收不等于款项已经到账。</strong>
            已确认实收、等待核对、核对异常和未结金额会分别列示；退款及已付款退货形成的应收调减也单独显示，不会静默混在一起。
          </p>
          <p>本页只读，只呈现同一份订单核账结果，不在页面重新计算或修改订单。</p>
        </div>
      </header>

      {detail.isFetching ? (
        <output className="empty-state" aria-live="polite">
          正在核对 {route.businessDate}
          的完整订单应收账本，确认前不会显示旧日期、旧账号或旧门店的金额。
        </output>
      ) : detail.isError || responseUnsafe ? (
        <div className="error-band" role="alert">
          <span>
            这项订单应收明细暂时无法安全读取，旧明细和金额已隐藏；只影响本项查看，不表示整套 ERP
            离线。
          </span>
          <button
            className="button secondary compact"
            type="button"
            onClick={() => detail.refetch()}
          >
            重新读取本项应收
          </button>
        </div>
      ) : visibleDetail ? (
        <>
          <section className="summary-grid" aria-label="营业日订单应收汇总">
            <div>
              <span>订单数</span>
              <strong>{visibleDetail.summary.orderCount} 单</strong>
            </div>
            <div>
              <span>系统应收总额</span>
              <strong>{formatMoney(visibleDetail.summary.receivableFen)}</strong>
            </div>
            <div>
              <span>已确认实收</span>
              <strong>{formatMoney(visibleDetail.summary.confirmedReceiptFen)}</strong>
            </div>
            <div>
              <span>等待渠道核对</span>
              <strong>{formatMoney(visibleDetail.summary.pendingReconciliationFen)}</strong>
            </div>
            <div>
              <span>收款核对异常</span>
              <strong>{formatMoney(visibleDetail.summary.exceptionReconciliationFen)}</strong>
            </div>
            <div>
              <span>未结应收</span>
              <strong>
                {visibleDetail.summary.unsettledOrderCount} 单 ·{" "}
                {formatMoney(visibleDetail.summary.unsettledFen)}
              </strong>
            </div>
            <div>
              <span>退款</span>
              <strong>
                {visibleDetail.summary.refundedOrderCount} 单 ·{" "}
                {formatMoney(visibleDetail.summary.refundedFen)}
              </strong>
            </div>
            <div>
              <span>已付款退货应收调减</span>
              <strong>{formatMoney(visibleDetail.summary.paidReturnAdjustmentFen)}</strong>
            </div>
            <div>
              <span>未说明金额差异</span>
              <strong>{formatSignedMoney(visibleDetail.summary.unexplainedDifferenceFen)}</strong>
            </div>
            <div>
              <span>异常订单数</span>
              <strong>{visibleDetail.summary.anomalyOrderCount} 单</strong>
            </div>
            <div>
              <span>最近生成时间</span>
              <strong>
                <time dateTime={visibleDetail.generatedAt}>
                  {formatTime(visibleDetail.generatedAt)}
                </time>
              </strong>
            </div>
          </section>

          {visibleDetail.orders.length === 0 ? (
            <section className="empty-state" aria-label="营业日订单应收为空">
              <strong>该营业日没有订单应收记录</strong>
              <p>
                完整订单账本已核对；这里只表示本项没有订单，不代表其他营业日或其他经营数据为零。
              </p>
            </section>
          ) : (
            <section aria-label="营业日逐单应收核对">
              <div className="commerce-section-title">
                <div>
                  <h2>逐单应收与资金去向</h2>
                  <p>每一行均来自同一份完整订单核账结果，可用公开订单号回到原订单继续核对。</p>
                </div>
              </div>
              <div className="member-lifecycle-timeline-list">
                {visibleDetail.orders.map((order, index) => {
                  const row = visibleDetail.rows[index];
                  if (!row) return null;
                  const status = orderStatusTerm(order.status);
                  return (
                    <article key={order.id}>
                      <div>
                        <strong>
                          订单{" "}
                          {order.orderNumber ? (
                            <Link
                              className="member-lifecycle-order-link"
                              to={`/orders?orderQuery=${encodeURIComponent(order.orderNumber)}`}
                            >
                              {order.orderNumber}
                            </Link>
                          ) : (
                            "公开订单编号待补充"
                          )}
                        </strong>
                        <span>{status.label}</span>
                      </div>
                      <dl>
                        <div>
                          <dt>订单营业日</dt>
                          <dd>{order.businessDate}</dd>
                        </div>
                        <div>
                          <dt>桌号</dt>
                          <dd>{order.tableNumber}</dd>
                        </div>
                        <div>
                          <dt>订单状态</dt>
                          <dd>
                            <strong>{status.label}</strong>
                            <p>{status.explanation}</p>
                          </dd>
                        </div>
                        <div>
                          <dt>系统应收</dt>
                          <dd>{formatMoney(row.receivableFen)}</dd>
                        </div>
                        <div>
                          <dt>已确认实收</dt>
                          <dd>{formatMoney(row.confirmedReceiptFen)}</dd>
                        </div>
                        <div>
                          <dt>等待渠道核对</dt>
                          <dd>{formatMoney(row.pendingReconciliationFen)}</dd>
                        </div>
                        <div>
                          <dt>收款核对异常</dt>
                          <dd>{formatMoney(row.exceptionReconciliationFen)}</dd>
                        </div>
                        <div>
                          <dt>未结应收</dt>
                          <dd>{formatMoney(row.unsettledFen)}</dd>
                        </div>
                        <div>
                          <dt>退款</dt>
                          <dd>{formatMoney(row.refundedFen)}</dd>
                        </div>
                        <div>
                          <dt>已付款退货应收调减</dt>
                          <dd>{formatMoney(row.paidReturnAdjustmentFen)}</dd>
                        </div>
                        <div>
                          <dt>未说明金额差异</dt>
                          <dd>{formatSignedMoney(row.unexplainedDifferenceFen)}</dd>
                        </div>
                        <div>
                          <dt>异常原因</dt>
                          <dd>
                            {row.anomalyCodes.length === 0 ? (
                              "没有发现需要处理的异常"
                            ) : (
                              <ul>
                                {row.anomalyCodes.map((code) => {
                                  const anomaly = orderAnomalyTerm(code);
                                  return (
                                    <li key={code}>
                                      <strong>{anomaly.label}</strong>
                                      <p>
                                        {anomaly.explanation}
                                        {anomaly.nextStep}
                                      </p>
                                    </li>
                                  );
                                })}
                              </ul>
                            )}
                          </dd>
                        </div>
                      </dl>
                    </article>
                  );
                })}
              </div>
            </section>
          )}
        </>
      ) : null}
    </main>
  );
}

export function OrderReceivableBusinessMetricDetailPanel({
  operator,
  effectiveRoles,
  route,
  loader = getOrderReceivableBusinessMetricDetail,
}: OrderReceivableBusinessMetricDetailPanelProps): React.JSX.Element {
  if (route.kind === "INVALID") {
    return (
      <main className="commercial-desk" aria-label="订单应收明细链接无效">
        <div className="error-band" role="alert">
          <span>订单应收明细的营业日期无法安全确认，系统没有读取或展示普通订单列表。</span>
          <div className="button-row">
            <Link className="button secondary compact" to="/owner">
              返回经营指标
            </Link>
            <Link className="button secondary compact" to="/orders">
              返回订单页
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const authorized = effectiveRoles.some((role) => role === "OWNER" || role === "MANAGER");
  if (!authorized) {
    return (
      <main className="commercial-desk" aria-label="订单应收明细无查看权限">
        <div className="error-band" role="alert">
          <span>当前账号不能查看全店订单应收明细，系统没有读取普通订单或资金金额。</span>
          <Link className="button secondary compact" to="/orders">
            返回订单页
          </Link>
        </div>
      </main>
    );
  }

  return (
    <AuthorizedOrderReceivableBusinessMetricDetail
      key={`${operator.tenantId}\u0000${operator.storeId}\u0000${operator.id}\u0000${route.businessDate}`}
      operator={operator}
      route={route}
      loader={loader}
    />
  );
}
